import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { TabulatorFull as Tabulator } from 'tabulator-tables';
import TutorialButton from './common/TutorialButton';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { tratarErroAPI, logarRequisicao, logarSucesso } from '../utils/errorHandler';
import '../styles/imagem.css';
import etgLogo from '../assets/etg.png';
import ufmgLogo from '../assets/Ufmg-sem nome-03.png';
import '../styles/home.css';

const ImagemManager = () => {
  const navigate = useNavigate();
  const [dados, setDados] = useState([]);
  const [loading, setLoading] = useState(false);
  const [mensagem, setMensagem] = useState({ tipo: '', texto: '' });
  const [formData, setFormData] = useState({
    id_escola: '',
    file: null,
    principal: false
  });
  const [apiStatus, setApiStatus] = useState('checking');
  const [lastUpdate, setLastUpdate] = useState(null);
  const [escolaSearch, setEscolaSearch] = useState('');
  const [escolas, setEscolas] = useState([]);

  const tabulatorRef = useRef(null);
  const tableRef = useRef(null);
  const fileInputRef = useRef(null);

  // Filtrar escolas com base na busca
  const escolasFiltradas = useMemo(() => {
    if (!escolaSearch) return escolas;
    return escolas.filter(escola =>
      escola.nome_escola?.toLowerCase().includes(escolaSearch.toLowerCase()) ||
      escola.id_escola?.toString().includes(escolaSearch) ||
      escola.codigo_inep?.toString().includes(escolaSearch)
    );
  }, [escolas, escolaSearch]);

  const mostrarMensagem = useCallback((tipo, texto) => {
    setMensagem({ tipo, texto });
    setTimeout(() => setMensagem({ tipo: '', texto: '' }), 4000);
  }, []);

  const fetchEscolas = useCallback(async () => {
    try {
      const response = await api.get('/escola');
      setEscolas(response.data.data || []);
    } catch (error) {
      mostrarMensagem('error', `Erro ao carregar escolas: ${error.message}`);
    }
  }, [mostrarMensagem]);

  const verificarAPI = useCallback(async () => {
    try {
      setApiStatus('checking');
      await api.get('/imagem');
      setApiStatus('connected');
    } catch (error) {
      setApiStatus('error');
      mostrarMensagem('error', `Erro de conexão: ${error.message}`);
    }
  }, [mostrarMensagem]);

  const carregarDados = useCallback(async (showMessage = true) => {
    try {
      setLoading(true);
      const response = await api.get('/imagem');
      setDados(response.data.data || []);
      setLastUpdate(new Date());
      if (showMessage) {
        mostrarMensagem('success', `${response.data.data?.length || 0} registros carregados`);
      }
    } catch (error) {
      mostrarMensagem('error', error.message);
      setDados([]);
    } finally {
      setLoading(false);
    }
  }, [mostrarMensagem]);

  useEffect(() => {
    verificarAPI();
    fetchEscolas();
    carregarDados();
  }, [verificarAPI, fetchEscolas, carregarDados]);

  const atualizarRegistro = useCallback(async (id, dadosAtualizacao) => {
    logarRequisicao('Atualizar', 'imagem', { id, dadosAtualizacao });
    try {
      const dadosValidos = { ...dadosAtualizacao };
      if (dadosValidos.data_upload === '') {
        dadosValidos.data_upload = null;
      }

      const response = await api.put(`/imagem/${id}`, dadosValidos);
      logarSucesso('Atualizar', 'imagem', response.data);

      if (response && response.data) {
        if (tabulatorRef.current) {
          tabulatorRef.current.updateData([response.data.data]);
        }
        mostrarMensagem('success', 'Registro atualizado com sucesso!');
        carregarDados(false);
      } else {
        mostrarMensagem('error', 'Resposta da API inválida ao atualizar.');
      }
    } catch (error) {
      const mensagemErro = tratarErroAPI(error, 'atualizar', 'imagem');
      mostrarMensagem('error', mensagemErro);
    }
  }, [mostrarMensagem, carregarDados]);

  const excluirRegistro = useCallback(async (id) => {
    logarRequisicao('Excluir', 'imagem', { id });
    try {
      const response = await api.delete(`/imagem/${id}`);
      logarSucesso('Excluir', 'imagem', response.data);
      setDados(currentData => currentData.filter(item => item.id_imagem !== id));
      mostrarMensagem('success', 'Registro excluído com sucesso!');
    } catch (error) {
      const mensagemErro = tratarErroAPI(error, 'excluir', 'imagem');
      mostrarMensagem('error', mensagemErro);
    }
  }, [mostrarMensagem]);

  const getColunas = useMemo(() => {
    const escolasNomes = escolas.reduce((acc, item) => ({ ...acc, [item.id_escola]: item.nome_escola }), {});

    const colunasBase = [
      {
        title: 'ID Imagem',
        field: 'id_imagem',
        width: 180,
        headerFilter: 'input',
        headerFilterPlaceholder: 'Filtrar ID...',
        sorter: 'string'
      },
      {
        title: 'Chave do Arquivo',
        field: 'arquivo_chave',
        width: 220,
        headerFilter: 'input',
        headerFilterPlaceholder: 'Filtrar chave...',
        sorter: 'string',
      },
      {
        title: 'Data Upload',
        field: 'data_upload',
        headerFilter: 'input',
        headerFilterPlaceholder: 'Filtrar data...',
        sorter: 'date',
        width: 200,
        headerFilterFunc: (headerValue, rowValue, rowData, filterParams) => {
          if (!headerValue) return true;
          if (!rowValue) return false;

          try {
            const date = new Date(rowValue);
            if (isNaN(date.getTime())) return rowValue.includes(headerValue);

            const day = String(date.getUTCDate()).padStart(2, '0');
            const month = String(date.getUTCMonth() + 1).padStart(2, '0');
            const year = date.getUTCFullYear();
            const formattedDate = `${day}/${month}/${year}`;

            return formattedDate.includes(headerValue);
          } catch (e) {
            return false;
          }
        },
        editor: (cell, onRendered, success, cancel) => {
          const input = document.createElement("input");
          input.setAttribute("type", "text");
          input.setAttribute("placeholder", "DD/MM/YYYY");
          input.style.padding = "4px";
          input.style.width = "100%";
          input.style.boxSizing = "border-box";

          const value = cell.getValue();
          if (value) {
            try {
              const date = new Date(value);
              if (!isNaN(date.getTime())) {
                const day = String(date.getUTCDate()).padStart(2, '0');
                const month = String(date.getUTCMonth() + 1).padStart(2, '0');
                const year = date.getUTCFullYear();
                input.value = `${day}/${month}/${year}`;
              } else {
                input.value = value;
              }
            } catch (e) {
              input.value = value;
            }
          }

          onRendered(() => {
            input.focus();
            input.style.height = "100%";
          });

          const handleDateInput = (e) => {
            const input = e.target;
            let value = input.value.replace(/\D/g, '');
            let formattedValue = '';
            if (value.length > 0) {
              formattedValue = value.substring(0, 2);
            }
            if (value.length > 2) {
              formattedValue += '/' + value.substring(2, 4);
            }
            if (value.length > 4) {
              formattedValue += '/' + value.substring(4, 8);
            }
            input.value = formattedValue;
          };

          input.addEventListener('input', handleDateInput);

          const onChange = () => {
            if (input.value && input.value.match(/^(\d{2})\/(\d{2})\/(\d{4})$/)) {
              const [day, month, year] = input.value.split('/');
              success(`${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`);
            } else {
              success(input.value);
            }
          };

          input.addEventListener("change", onChange);
          input.addEventListener("blur", onChange);

          return input;
        },
        formatter: (cell) => {
          const value = cell.getValue();
          if (!value) return '';
          try {
            const date = new Date(value);
            if (isNaN(date.getTime())) return value;
            const day = String(date.getUTCDate()).padStart(2, '0');
            const month = String(date.getUTCMonth() + 1).padStart(2, '0');
            const year = date.getUTCFullYear();
            return `${day}/${month}/${year}`;
          } catch (e) {
            return value;
          }
        }
      },
      {
        title: 'Nome Externo',
        field: 'nome_externo',
        width: 200,
        headerFilter: 'input',
        headerFilterPlaceholder: 'Filtrar nome...',
        sorter: 'string',
      },
      {
        title: 'Usuário',
        field: 'usuario',
        width: 150,
        headerFilter: 'input',
        headerFilterPlaceholder: 'Filtrar usuário...',
        sorter: 'number',
      },
      {
        title: 'Descrição',
        field: 'descricao',
        width: 300,
        headerFilter: 'input',
        headerFilterPlaceholder: 'Filtrar descrição...',
        sorter: 'string',
      },
      {
        title: 'Escola',
        field: 'id_escola',
        width: 300,
        headerFilter: 'input',
        headerFilterPlaceholder: 'Filtrar Escola...',
        sorter: 'string',
        formatter: (cell) => escolasNomes[cell.getValue()] || cell.getValue(),
        headerFilterFunc: (headerValue, rowValue, rowData, filterParams) => {
          if (!headerValue) return true;
          const nomeEscola = escolasNomes[rowValue] || rowValue || '';
          return nomeEscola.toLowerCase().includes(headerValue.toLowerCase());
        },
        editor: 'list',
        editorParams: { values: escolasNomes }
      },
      {
        title: 'Principal',
        field: 'principal',
        width: 120,
        hozAlign: 'center',
        formatter: 'tickCross',
        sorter: 'boolean',
        editor: 'tickCross'
      },
      {
        title: 'Ativa',
        field: 'ativa',
        width: 100,
        hozAlign: 'center',
        formatter: 'tickCross',
        sorter: 'boolean',
        editor: 'tickCross'
      }
    ];

    return [
      {
        title: 'Salvar',
        field: 'salvar',
        width: 80,
        hozAlign: 'center',
        formatter: () => '<button class="btn-save"><i class="fas fa-save"></i></button>',
        cellClick: (e, cell) => {
          const data = cell.getRow().getData();
          atualizarRegistro(data.id_imagem, data);
        },
        headerSort: false
      },
      {
        title: 'Excluir',
        field: 'excluir',
        width: 80,
        hozAlign: 'center',
        formatter: () => '<button class="btn-delete"><i class="fas fa-trash"></i></button>',
        cellClick: (e, cell) => {
          const data = cell.getRow().getData();
          if (window.confirm(`Deseja realmente excluir o registro "${data.id_imagem}"?`)) {
            excluirRegistro(data.id_imagem);
          }
        },
        headerSort: false
      },
      ...colunasBase.map(col => ({ ...col, editor: col.editor || 'input' }))
    ];
  }, [escolas, atualizarRegistro, excluirRegistro]);

  useEffect(() => {
    if (apiStatus === 'connected') {
      carregarDados(false);
    }
  }, [apiStatus, carregarDados]);

  const inicializarTabulator = useCallback(() => {
    if (!tableRef.current) return;
    if (tabulatorRef.current) tabulatorRef.current.destroy();

    tabulatorRef.current = new Tabulator(tableRef.current, {
      data: dados,
      columns: getColunas,
      layout: 'fitColumns',
      pagination: 'local',
      paginationSize: 10,
      paginationCounter: 'rows',
      movableColumns: true,
      resizableRows: true,
      tooltips: true,
      placeholder: 'Nenhum registro encontrado',
      index: 'id_imagem'
    });

    const style = document.createElement('style');
    style.textContent = `
      .btn-save {
        background: #007bff;
        color: white;
        border: none;
        padding: 5px 10px;
        border-radius: 3px;
        cursor: pointer;
        font-size: 12px;
      }
      .btn-save:hover {
        background: #0056b3;
      }
      .btn-delete {
        background: #dc3545;
        color: white;
        border: none;
        padding: 5px 10px;
        border-radius: 3px;
        cursor: pointer;
        font-size: 12px;
      }
      .btn-delete:hover {
        background: #c82333;
      }
      .btn-back {
        background: transparent;
        border: none;
        font-size: 24px;
        cursor: pointer;
      }
    `;
    document.head.appendChild(style);
  }, [dados, getColunas]);

  useEffect(() => {
    if (apiStatus === 'connected' && tableRef.current) {
      if (!tabulatorRef.current) {
        inicializarTabulator();
      } else {
        tabulatorRef.current.setColumns(getColunas);
        tabulatorRef.current.setData(dados);
      }
    }
  }, [dados, apiStatus, inicializarTabulator, getColunas]);

  const handleUpload = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (!formData.file) {
        throw new Error('Selecione uma imagem');
      }
      if (!formData.id_escola) {
        throw new Error('Selecione uma escola');
      }

      const data = new FormData();
      data.append('file', formData.file);
      data.append('principal', formData.principal);

      // Recuperar usuário do localStorage
      const user = JSON.parse(localStorage.getItem('user'));
      if (user && user.id) {
        data.append('usuario', user.id);
      }

      const baseUrl = api.defaults.baseURL;
      const url = `${baseUrl}/uploads/escolas/${formData.id_escola}`;

      const token = localStorage.getItem('accessToken');
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: data
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.message || 'Erro no upload');
      }

      mostrarMensagem('success', 'Imagem enviada com sucesso!');

      // Resetar form
      setFormData(prev => ({
        ...prev,
        file: null,
        principal: false
      }));
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }

      // Recarregar dados
      carregarDados(false);

    } catch (error) {
      console.error(error);
      mostrarMensagem('error', error.message || 'Erro ao enviar imagem.');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleFileChange = (e) => {
    if (e.target.files[0]) {
      setFormData(prev => ({ ...prev, file: e.target.files[0] }));
    }
  };

  const exportarDados = () => {
    if (tabulatorRef.current) {
      tabulatorRef.current.download('csv', 'imagem.csv');
    }
  };

  return (
    <div className="tipo-ambiente-container">
      <div className="header-controls">
        <button className="btn-back" onClick={() => navigate('/')}><i className="fas fa-arrow-left"></i></button>
      </div>

      <div className="header">
        <div className="title-container">
          <img src={etgLogo} alt="ETG Logo" className="logo" />
          <h1>Upload de Imagens</h1>
          <img src={ufmgLogo} alt="UFMG Logo" className="logo" />
        </div>
        <p>Upload de imagens de escolas</p>
        <div style={{ marginTop: '10px' }}>
          <TutorialButton videoPath="bdados/imagem.mov" buttonText="Ver Tutorial Imagem" />
        </div>
      </div>

      {mensagem.texto && (
        <div className="status-container">
          <div className={`status ${mensagem.tipo}`}>{mensagem.texto}</div>
        </div>
      )}

      <div className="controls">
        <form onSubmit={handleUpload} className="form-inline">
          <div className="form-group">
            <label htmlFor="imagem_file">Selecione a Imagem:<span style={{ color: 'red', marginLeft: '2px' }}>*</span></label>
            <input
              type="file"
              id="imagem_file"
              ref={fileInputRef}
              onChange={handleFileChange}
              accept="image/*"
              required
              disabled={loading || apiStatus !== 'connected'}
            />
          </div>
          <div className="form-group">
            <label htmlFor="escola_search">Escola:<span style={{ color: 'red', marginLeft: '2px' }}>*</span></label>
            <input
              type="text"
              id="escola_search"
              name="escola_search"
              value={escolaSearch}
              onChange={(e) => {
                setEscolaSearch(e.target.value);
                const escolaEncontrada = escolas.find(esc => esc.nome_escola === e.target.value);
                if (escolaEncontrada) {
                  setFormData(prev => ({ ...prev, id_escola: escolaEncontrada.id_escola }));
                } else {
                  setFormData(prev => ({ ...prev, id_escola: '' }));
                }
              }}
              list="escolas-list-imagem"
              placeholder="Digite para buscar escola..."
              autoComplete="off"
              disabled={loading || apiStatus !== 'connected'}
            />
            <datalist id="escolas-list-imagem">
              {escolasFiltradas.map(item => (
                <option key={item.id_escola} value={item.nome_escola} />
              ))}
            </datalist>
          </div>
          <div className="form-group">
            <label htmlFor="principal">Imagem Principal:</label>
            <input
              type="checkbox"
              id="principal"
              name="principal"
              checked={formData.principal}
              onChange={handleInputChange}
              disabled={loading || apiStatus !== 'connected'}
            />
          </div>

          <div className="form-actions-row">
            <div className="form-buttons">
              <button type="submit" className="btn btn-success" disabled={loading || apiStatus !== 'connected'}>
                {loading ? '⏳ Enviando...' : '⬆️ Enviar Imagem'}
              </button>
              <button type="button" className="btn btn-primary" onClick={() => carregarDados(true)} disabled={loading || apiStatus !== 'connected'}>
                {loading ? '⏳ Carregando...' : '🔄 Recarregar'}
              </button>
              <button type="button" className="btn btn-secondary" onClick={exportarDados} disabled={dados.length === 0}>
                📥 Exportar CSV
              </button>
            </div>
            <div className="info-campos-obrigatorios">
              <i className="fas fa-info-circle"></i>
              <span>* indica que os campos são obrigatorios.</span>
            </div>
          </div>

        </form>
      </div>

      <div className="stats-bar">
        <div className="stats-item"><strong>{dados.length}</strong> registros carregados</div>
        <div className="stats-item">Última atualização: <strong>{lastUpdate ? lastUpdate.toLocaleDateString('pt-BR') : 'N/A'}</strong></div>
      </div>

      <div className="tabulator-container">
        <div ref={tableRef}></div>
      </div>

    </div>
  );
};

export default ImagemManager;
